import React from "react";
import PropTypes from "prop-types";
import { FormikProvider } from "formik";

import DefineRolesAndShiftsFormBody from "./DefineRolesAndShiftsFormBody";
import DefineRolesAndShiftsIntro from "./DefineRolesAndShiftsIntro";
import DefineRolesAndShiftsModals from "./DefineRolesAndShiftsModals";
import { useDefineRolesAndShifts } from "./useDefineRolesAndShifts";

import "./DefineRolesAndShifts.scss";

const DefineRolesAndShifts = (props) => {
  const {
    formik,
    t,
    creatEventLabels,
    shared,
    event,
    emptyRolesRef,
    roles,
    rolesOptions,
    activeRoleIndex,
    activeRole,
    activeShiftIndex,
    invalidRoleIndexes,
    validationAttempt,
    activeRoleValidated,
    emptyRolesAttention,
    isRoleAccordionExpanded,
    setActiveRoleIndex,
    setIsRoleAccordionExpanded,
    rolePendingRemoval,
    setRolePendingRemoval,
    shiftPendingRemoval,
    setShiftPendingRemoval,
    paidTrainingRemoval,
    setPaidTrainingRemoval,
    eventLocationBoundaryMeters,
    portalCompanyOngoingEdit,
    companyPortalMode,
    isEditMode,
    roleRemovalLockedNearEventStart,
    handleAddRole,
    handleRequestRemoveRole,
    handleRemoveRole,
    handleAddShift,
    handleRemoveShift,
    handleShiftChange,
    handleTrainingToggle,
    handleConfirmRemovePaidTraining,
  } = useDefineRolesAndShifts(props);

  return (
    <FormikProvider value={formik}>
      <form className="define-roles-step" onSubmit={formik.handleSubmit}>
        <DefineRolesAndShiftsIntro
          pageTitle={t.pageTitle}
          pageSubtitle={t.pageSubtitle}
        />

        <DefineRolesAndShiftsFormBody
          formik={formik}
          roles={roles}
          rolesOptions={rolesOptions}
          activeRoleIndex={activeRoleIndex}
          activeRole={activeRole}
          activeShiftIndex={activeShiftIndex}
          invalidRoleIndexes={invalidRoleIndexes}
          validationAttempt={validationAttempt}
          activeRoleValidated={activeRoleValidated}
          emptyRolesAttention={emptyRolesAttention}
          emptyRolesRef={emptyRolesRef}
          isRoleAccordionExpanded={isRoleAccordionExpanded}
          onSelectRole={(index) => {
            setActiveRoleIndex(index);
            setIsRoleAccordionExpanded(true);
          }}
          onAddRole={handleAddRole}
          onAccordionExpandedChange={setIsRoleAccordionExpanded}
          onRemoveRole={handleRequestRemoveRole}
          onShiftChange={handleShiftChange}
          onAddShift={handleAddShift}
          onRemoveShift={() =>
            setShiftPendingRemoval({
              roleIndex: activeRoleIndex,
              shiftIndex: activeShiftIndex,
            })
          }
          onTrainingToggle={handleTrainingToggle}
          eventLocationBoundaryMeters={eventLocationBoundaryMeters}
          eventRadius={event?.event_radius}
          removeRoleDisabled={false}
          portalCompanyOngoingEdit={portalCompanyOngoingEdit}
          companyPortalMode={companyPortalMode}
          isEditMode={isEditMode}
          addAnotherRoleLabel={t.addAnotherRole}
        />

        <DefineRolesAndShiftsModals
          isEditMode={isEditMode}
          rolePendingRemoval={rolePendingRemoval}
          shiftPendingRemoval={shiftPendingRemoval}
          paidTrainingRemoval={paidTrainingRemoval}
          onCloseRoleRemoval={() => setRolePendingRemoval(null)}
          onConfirmRoleRemoval={handleRemoveRole}
          onCloseShiftRemoval={() => setShiftPendingRemoval(null)}
          onConfirmShiftRemoval={handleRemoveShift}
          onClosePaidTrainingRemoval={() => setPaidTrainingRemoval(null)}
          onConfirmPaidTrainingRemoval={handleConfirmRemovePaidTraining}
          roleRemovalLabels={{
            title: t.removeRole,
            message: t.removeRoleMessage,
            withinLaunchWindowMessage: t.removeRoleWithinLaunchWindowMessage,
            confirmLabel: t.confirmRemoval,
          }}
          shiftRemovalLabels={{
            title: t.removeShift,
            message: t.removeShiftMessage,
            confirmLabel: t.remove,
          }}
          paidTrainingModal={{
            title: t.paidTraining,
            message: creatEventLabels?.deletePaidTrainingModalMessage ?? "",
            confirmLabel: shared?.delete ?? "Delete",
          }}
          roleRemovalLockedNearEventStart={roleRemovalLockedNearEventStart}
        />
      </form>
    </FormikProvider>
  );
};

export default DefineRolesAndShifts;

DefineRolesAndShifts.propTypes = {
  isEditMode: PropTypes.bool,
  registerSubmit: PropTypes.func,
  registerStepApi: PropTypes.func,
  rolesOptions: PropTypes.arrayOf(PropTypes.object),
  eventScheduleBounds: PropTypes.shape({
    eventStartAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    eventEndAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  initialEvent: PropTypes.object,
  requestsPortal: PropTypes.oneOf(["admin", "company"]),
  /** From Basic Info `location.radius` — seeds paid-training boundary */
  eventLocationBoundaryMeters: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  companyPortalMode: PropTypes.bool,
};

DefineRolesAndShifts.defaultProps = {
  isEditMode: false,
  registerSubmit: undefined,
  registerStepApi: undefined,
  rolesOptions: [],
  eventScheduleBounds: undefined,
  initialEvent: undefined,
  requestsPortal: "admin",
  eventLocationBoundaryMeters: "",
  companyPortalMode: false,
};
